import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="19793" className="flex gap-4 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-muted rounded" />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="text-base font-sans font-semibold transition-colors hover:text-accent focus:text-accent text-primary">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return <div className="p-4 text-center text-primary font-sans">Loading menu...</div>;
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block py-3 text-lg font-sans font-bold border-b border-border/50 last:border-0 text-primary hover:text-accent" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const phoneNumber = "(715) 688-9336";
  const businessName = "Finely Done";
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-md" : "bg-background")}>
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/HomePage" className="flex items-center gap-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576980/img/attic_to_basement_pest_control_logo.png" alt={businessName} className="h-10 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenu />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex items-center gap-6">
            <Link to={`tel:${phoneNumber.replace(/\D/g, "")}`} className="flex items-center gap-2 text-xl font-sans font-bold text-primary hover:text-accent transition-colors">
              <Phone className="h-5 w-5 fill-current" />
              <span>{phoneNumber}</span>
            </Link>
            <Button size="lg" className="font-sans font-bold text-base shadow-lg bg-accent text-accent-foreground hover:bg-accent/90 border-none" asChild>
              <Link to="/ContactPage">Call Now</Link>
            </Button>
          </div>

          {}
          <div className="flex lg:hidden items-center gap-4">
            <Link to={`tel:${phoneNumber.replace(/\D/g, "")}`} className="flex items-center gap-1.5 font-sans font-bold text-primary">
              <Phone className="h-5 w-5 fill-current" />
              <span className="text-lg">{phoneNumber}</span>
            </Link>
            
            <Button variant="ghost" size="icon" className="md:hidden text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-background border-b border-border shadow-xl animate-in slide-in-from-top-5 fade-in duration-300">
          <MenuProvider menu_id="16">
            <MobileMenu onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-4 pt-0 flex flex-col gap-3">
             <Button className="w-full text-base font-sans font-bold bg-accent text-accent-foreground hover:bg-accent/90" size="lg" asChild>
              <Link to="/ContactPage">Request Your Quote</Link>
            </Button>
          </div>
        </div>}
    </header>;
}